import { useEffect, useRef } from "react";
import api from "../lib/api";

/**
 * Iter-30 — Heartbeat de tracking du temps réel d'apprentissage.
 *
 * Envoie un POST /api/learning/heartbeat toutes les N secondes tant que
 * la page est visible (Page Visibility API) et qu'un module/leçon est
 * actif. Le backend agrège ces fenêtres dans learning_time_log pour
 * détecter les "speeders" (cf admin_user_learning_time).
 *
 * Usage :
 *   useLearningHeartbeat({ moduleId, lessonId });
 *
 * Stop : passer moduleId=null pour arrêter (cleanup auto à l'unmount).
 */
const HEARTBEAT_INTERVAL_MS = 30 * 1000; // 30s par fenêtre

export default function useLearningHeartbeat({ moduleId, lessonId } = {}) {
    const timerRef = useRef(null);
    const lastTickRef = useRef(Date.now());

    useEffect(() => {
        if (!moduleId) return undefined;

        const sendBeat = async (seconds = 30) => {
            try {
                await api.post("/learning/heartbeat", {
                    module_id: moduleId,
                    lesson_id: lessonId || null,
                    seconds: Math.max(5, Math.min(60, seconds)),
                });
            } catch (_) {
                /* silent — ne doit jamais casser l'UX */
            }
        };

        const startTimer = () => {
            if (timerRef.current) return;
            lastTickRef.current = Date.now();
            timerRef.current = setInterval(() => {
                if (document.visibilityState !== "visible") return;
                sendBeat(30);
                lastTickRef.current = Date.now();
            }, HEARTBEAT_INTERVAL_MS);
        };
        const stopTimer = () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null;
            }
        };

        const onVisibility = () => {
            if (document.visibilityState === "visible") {
                startTimer();
            } else {
                // Flush la fenêtre partielle avant pause (max 60s)
                const elapsed = Math.round((Date.now() - lastTickRef.current) / 1000);
                if (elapsed >= 5) sendBeat(Math.min(elapsed, 60));
                stopTimer();
            }
        };

        // Démarre seulement si visible
        if (document.visibilityState === "visible") {
            startTimer();
        }
        document.addEventListener("visibilitychange", onVisibility);

        return () => {
            // Flush final à l'unmount
            const elapsed = Math.round((Date.now() - lastTickRef.current) / 1000);
            if (elapsed >= 5) sendBeat(Math.min(elapsed, 60));
            stopTimer();
            document.removeEventListener("visibilitychange", onVisibility);
        };
    }, [moduleId, lessonId]);
}
